import { z } from 'zod';
import { IdDescriptionItemSchema } from './iworklist.validation';
import { TelemonitoringExamination } from '../../../../se-modules-telemonitoring/src/lib/telemonitoring.models';

// Schema for IdDescriptionItemTelemonitoring (extends IdDescriptionItem with any id)
export const IdDescriptionItemTelemonitoringSchema = z
    .object({
        id: z.any(), // TelemonitoringExamination uses 'any' for id fields
        description: z.string().nullable(),
    })
    .strict();

// Schema for BusinessUnit (Telemonitoring-specific)
export const TelemonitoringBusinessUnitSchema = z
    .object({
        id: z.any(),
        description: z.string().nullable(),
        code: z.number(),
        httpPacs: z.string().nullable(),
        telemonitoringHeader: z.string().nullable(),
        telemonitoringVersioningText: z.string().nullable(),
        telemonitoringSignatureText: z.string().nullable(),
        telemonitoringLogoImgName: z.string().nullable(),
    })
    .strict();

// Schema for TelemonitoringProvenance
export const TelemonitoringProvenanceSchema = z
    .object({
        id: z.any(),
        description: z.string().nullable(),
        groupProvenance: IdDescriptionItemTelemonitoringSchema,
    })
    .strict();

// Schema for ICommandTelemonitoringExamination
export const ICommandTelemonitoringExaminationSchema = z
    .object({
        canBeDeferredAndDelete: z.boolean().optional(),
        canBeSuspended: z.boolean(),
        canBeUndoSuspended: z.boolean(),
        canEditExam: z.boolean().optional(),
        isMandatoryEquipmentChange: z.boolean().optional(),
        canBeResigned: z.boolean(),
        canUndoValidation: z.boolean().optional(),
    })
    .strict();

// Schema for TelemonitoringPatient (extends BasePatient with Telemonitoring-specific properties)
export const TelemonitoringPatientSchema = z
    .object({
        // From BasePatient
        id: z.any(), // Telemonitoring uses 'any' for id
        firstName: z.string().nullable(),
        lastName: z.string().nullable(),
        // From IBaseCommonPatient
        birthDate: z.any(), // Telemonitoring uses 'any' for birthDate
        sex: z.string().nullable(),
        idLocalPatient: z.string().nullable(),
        isLocalPatient: z.boolean(),
        sexCode: z.string().nullable(),
        taxCode: z.string().nullable(),
        // Telemonitoring-specific properties
        isCertificating: z.boolean().optional().nullable(),
        patientId: z.string(),
        // Missing properties from ICommonWorklistPatientDTO
        race: z.string().nullable(),
        otherPatientId: z.string(),
        residenceCity: z.string().nullable(),
        issuer: z.string().nullable(),
    })
    .strict();

// Schema for Exam (from vnext.models)
export const TelemonitoringExamSchema = z
    .object({
        id: z.string(),
        description: z.string().nullable(),
        isMonitoring: z.boolean().optional(),
        isRemoteVisit: z.boolean().optional(),
        isTelemonitoring: z.boolean().optional(),
        otherCode: z.string().optional().nullable(),
    })
    .strict();

// Schema for Severity (from vnext.models)
export const TelemonitoringSeveritySchema = z
    .object({
        id: z.string(),
        description: z.string().nullable(),
        color: z.string().nullable(),
    })
    .strict();

// Schema for User (from vnext.models)
export const TelemonitoringUserSchema = z
    .object({
        id: z.any(),
        fullName: z.string().nullable(),
        employeeSerialNumber: z.string().optional().nullable(),
        userName: z.string().optional().nullable(),
    })
    .strict();

// Complete schema for TelemonitoringExamination with all required properties
export const TelemonitoringExaminationSchema = z
    .object({
        // From IBaseCommonExamination
        id: z.string(),
        exam: TelemonitoringExamSchema,
        isMerged: z.boolean(),
        referringPhysician: TelemonitoringUserSchema.nullable(),
        reportDate: z.string().nullable(),
        severity: TelemonitoringSeveritySchema.nullable(),
        status: z.string().nullable(),
        idReport: z.string().nullable(),
        idLockedUser: z.string().nullable(),
        lockedHost: z.string().nullable(),
        lockedUserFullName: z.string().nullable(),
        // From BaseExamination
        examinationDate: z.string().nullable(),
        examinationsNumber: z.number().optional(),
        sameStudyInstanceNumber: z.number().optional(),
        isAdmitted: z.boolean(),
        isApproved: z.boolean(),
        isDigitallySigned: z.boolean(),
        isTelemonitoringReport: z.boolean(),
        isExecuted: z.boolean(),
        isReported: z.boolean(),
        imageAvailability: z.boolean(),
        locked: z.boolean(),
        examNumber: z.string().nullable(),

        // TelemonitoringExamination-specific properties
        reportingWithoutTrace: z.boolean(),
        patient: TelemonitoringPatientSchema,
        businessUnit: TelemonitoringBusinessUnitSchema,
        provenance: TelemonitoringProvenanceSchema,
        command: ICommandTelemonitoringExaminationSchema.optional(),

        // Additional properties to match requirements
        equipment: IdDescriptionItemSchema.optional(),
        diagnosis: IdDescriptionItemSchema.nullable(),
        admissionDate: z.string().nullable().optional(),
        executionDate: z.string().nullable().optional(),
        addendumNumber: z.number().optional(),
        fromIntegration: z.boolean().optional(),
        accessionNumber: z.string().nullable().optional(),
        revaluedCDA: z.boolean().nullable().optional(),
        monitoringPeriod: z.string().nullable().optional(),
        deviceType: z.string().nullable().optional(),
        transmissionDate: z.string().nullable().optional(),
    })
    .strict();

export const validateTelemonitoringExaminationDTO = (data: unknown): TelemonitoringExamination => {
    return TelemonitoringExaminationSchema.parse(data) as TelemonitoringExamination;
};

export const applyTelemonitoringExaminationValidation = (data: unknown, index: number) => {
    try {
        validateTelemonitoringExaminationDTO(data);
    } catch (error: any) {
        console.error(`Validation failed for telemonitoring examination at index ${index}:`, error.errors);
        throw new Error(`Invalid telemonitoring examination data at index ${index}: ${error.message}`);
    }
};
